import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { useRealtimeChannel, useRealtimeUserChannel } from '@/core/realtime';
import { notificationKeys } from './queries';
import type { NotificationPublication } from './types';

/**
 * Bind the caller's own realtime channel to the bell (FE-ADR-066): a new
 * notification refetches the bell's lists (badge included) and shows a toast
 * with its title — unless it is `quiet` (in-app switched off for its type,
 * BE-ADR-106); rows marked read elsewhere (another tab) refetch quietly. The channel comes from the server's connection grant, so it
 * is always the active org's (F1); with realtime off it is null and nothing is
 * registered — the bell's poll carries on alone.
 *
 * Returns whether the channel is live. Each time it becomes live — first
 * subscribe and every reconnect — the lists are refetched, since anything
 * published while it was down is not replayed (the broker keeps no history).
 *
 * Any other payload type is ignored: the server may add one before this
 * client knows about it.
 */
export function useNotificationsChannel(): boolean {
  const qc = useQueryClient();
  const channel = useRealtimeUserChannel();

  const live = useRealtimeChannel<NotificationPublication>(channel, (payload) => {
    if (payload?.type !== 'notification' && payload?.type !== 'notifications.read') return;
    void qc.invalidateQueries({ queryKey: notificationKeys.lists() });
    if (payload.type === 'notification' && payload.title && !payload.quiet) toast(payload.title);
  });

  useEffect(() => {
    if (live) void qc.invalidateQueries({ queryKey: notificationKeys.lists() });
  }, [live, qc]);

  return live;
}
